import type { ColumnDef } from '@tanstack/react-table'
import { DataTableColumnHeader } from '@/shared/components/data-table'
import { cn } from '@/shared/lib/utils'
import { formatHumanDate } from '@/shared/utils/format-date'
import type { AgencyAdminRow } from '../models/agency-admin'

export const AGENCIES_ADMIN_COLUMN_NAMES: Record<string, string> = {
  name: 'Agencia',
  code: 'Código',
  companyName: 'Empresa',
  active: 'Estado',
  updatedAt: 'Actualizado'
}

/**
 * Badge sobrio estilo Shopify: píldora neutra (gris) con un punto de color
 * discreto — el color vive en el punto, no grita en el fondo.
 */
export function AgencyActiveBadge({ active }: { active: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground/80">
      <span
        aria-hidden="true"
        className={cn(
          'h-1.5 w-1.5 shrink-0 rounded-full',
          active ? 'bg-emerald-500' : 'bg-neutral-400'
        )}
      />
      {active ? 'Activa' : 'Inactiva'}
    </span>
  )
}

/** Píldora discreta para la agencia predeterminada de su empresa. */
export function AgencyDefaultBadge() {
  return (
    <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground/70">
      Predeterminada
    </span>
  )
}

export const agenciesAdminColumns: ColumnDef<AgencyAdminRow>[] = [
  {
    accessorKey: 'name',
    header: ({ column }) => (
      <DataTableColumnHeader className="min-w-[220px]" column={column} name="Agencia" />
    ),
    cell: ({ row }) => (
      <span className="flex items-center gap-2">
        <span className="truncate font-medium" title={row.original.name}>
          {row.original.name}
        </span>
        {row.original.default && <AgencyDefaultBadge />}
      </span>
    )
  },
  {
    accessorKey: 'code',
    header: ({ column }) => <DataTableColumnHeader column={column} name="Código" />,
    cell: ({ row }) => <span className="whitespace-nowrap tabular-nums">{row.original.code}</span>
  },
  {
    accessorKey: 'companyName',
    header: ({ column }) => (
      <DataTableColumnHeader className="min-w-[220px]" column={column} name="Empresa" />
    ),
    cell: ({ row }) =>
      row.original.companyName ? (
        <span className="block max-w-[18rem] truncate" title={row.original.companyName}>
          {row.original.companyName}
        </span>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      )
  },
  {
    accessorKey: 'active',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} name="Estado" sortable={false} />
    ),
    cell: ({ row }) => <AgencyActiveBadge active={row.original.active} />
  },
  {
    accessorKey: 'updatedAt',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} name="Actualizado" sortable={false} />
    ),
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-muted-foreground">
        {formatHumanDate(row.original.updatedAt)}
      </span>
    )
  }
]
